import { useTranslations } from '@vocab/react';
import { Box, ContentBlock, Stack } from 'braid-design-system';
import { useCallback, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import {
  PAGE_ID_SELECT_AD_TYPE,
  PAGE_ID_CLASSIFY,
} from 'src/app/shared/constants/pageUid';
import {
  PRODUCT_SET_FAILED_UNAVAILABLE,
  PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE,
} from 'src/app/shared/constants/productSetStatus';
import { STANDOUT_SELECTION_STORAGE_KEY } from 'src/app/shared/constants/storage';
import {
  useFeatureToggleData,
  FEATURE_CART_AND_PAY_AND_POST_V2,
  FEATURE_TEST_SHOPPING_CART,
} from 'src/app/shared/featureToggle';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { isShoppingCartLoading } from 'src/app/utils/isShoppingCartLoading';
import { AdSelectionWrapper } from 'src/app/v1/views/components/AdSelectionWrapper';
import { Form } from 'src/app/v1/views/components/Form';
import { PageButtons } from 'src/app/v1/views/components/PageButtons';
import { withDesktopEnhancers } from 'src/app/v1/views/enhancers/withDesktopEnhancers';
import { useHandleEmailVerification } from 'src/app/v1/views/hooks/useHandleEmailVerification';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import type { AppState } from 'src/types/appState';
import type { CreateFlowPageProps } from 'src/types/pages';

import translations from './.vocab';
import { SelectAdTypeError } from './components/SelectAdTypeError';

import * as styles from './index.css';

export const SelectAdType = (props: CreateFlowPageProps) => {
  const { t } = useTranslations(translations);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { productSetStatus, selectedProductId } = useSelector(
    (state: AppState) => ({
      productSetStatus: state.session.productSetStatus,
      selectedProductId: state.formData.selectedProductId,
    }),
    shallowEqual,
  );

  const { handleEmailVerification } = useHandleEmailVerification();

  const isCartEnabled = useFeatureToggleData(FEATURE_CART_AND_PAY_AND_POST_V2);
  const isTestingCartEnabled = useFeatureToggleData(FEATURE_TEST_SHOPPING_CART);

  const { cartProductInProgress, cartState } = useShoppingCart();
  const isContinueButtonLoading =
    isLoading ||
    isShoppingCartLoading({
      cartProductInProgress,
      isCartAndPayAndPostPageV2FeatureEnabled: isCartEnabled,
      isTestShoppingCartFeatureEnabled: isTestingCartEnabled,
    });

  const handleValidation = async (skipIfNull = false) => {
    setIsLoading(true);

    await handleEmailVerification();

    setIsLoading(false);
    const hasProductBeenSelected = isCartEnabled
      ? Boolean(cartState?.items && cartState?.items.length > 0)
      : Boolean(selectedProductId);

    const message =
      !skipIfNull && !hasProductBeenSelected
        ? t('ad-type-page-validation-message')
        : null;

    setErrorMessage(message);

    return message;
  };

  const onAdProductSelected = useCallback(
    (selectedAdProductId: string) => {
      if (Boolean(selectedAdProductId) && errorMessage !== null) {
        setErrorMessage(null);
      }
      sessionStorage.removeItem(STANDOUT_SELECTION_STORAGE_KEY);
    },
    [errorMessage],
  );

  const onBackButtonClicked = () => {
    props.handleProgrammaticRouteChange(PAGE_ID_CLASSIFY);
  };

  const isUnableToSelectExistingProduct =
    productSetStatus === PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE;

  return (
    <PageLayout
      hasProgressBar
      handleDispatchDraft={props.handleDispatchDraft}
      handleProgrammaticRouteChange={props.handleProgrammaticRouteChange}
      handleValidation={handleValidation}
      pageErrorMessage={props.pageErrorMessage}
      isContentFullWidth
    >
      <Form
        handleNextPageRoute={props.handleNextPageRoute}
        handleValidation={handleValidation}
      >
        <Box marginBottom="large" marginTop="xlarge">
          <Stack space="xlarge">
            <Stack space="medium">
              <Stack space="medium">
                {isUnableToSelectExistingProduct && (
                  <ContentBlock width="medium">
                    <SelectAdTypeError tone="info">
                      {t('ad-type-page-alert-expired-text')}
                    </SelectAdTypeError>
                  </ContentBlock>
                )}

                <ContentBlock width="large">
                  <AdSelectionWrapper
                    onAdProductSelected={onAdProductSelected}
                  />
                </ContentBlock>
              </Stack>

              {Boolean(errorMessage) && (
                <ContentBlock>
                  <SelectAdTypeError>{errorMessage}</SelectAdTypeError>
                </ContentBlock>
              )}
            </Stack>
            {productSetStatus !== PRODUCT_SET_FAILED_UNAVAILABLE && (
              <ContentBlock>
                <Box marginBottom="xxlarge">
                  <PageButtons
                    className={styles.containerPageButtons}
                    isLoading={isContinueButtonLoading}
                    showBackButton={true}
                    onBackButtonClicked={onBackButtonClicked}
                    handleSaveDraftFailed={props.handleSaveDraftFailed}
                    handleValidation={handleValidation}
                  />
                </Box>
              </ContentBlock>
            )}
          </Stack>
        </Box>
      </Form>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_SELECT_AD_TYPE)(SelectAdType);
